// Analytics & Tracking Manager
// Handles Google Analytics, Facebook Pixel, and Google Ads
// Only loads scripts if user has given consent

// Configuration
const TRACKING_IDS = {
  GA_MEASUREMENT_ID: 'G-D02XJVBQ2G',
  FB_PIXEL_ID: '1277722889452910',
  GOOGLE_ADS_ID: '', // Will be added when provided
  GOOGLE_ADS_LABEL: '' // Will be added when provided
};

// Initialize Google Analytics (GA4)
export const initGoogleAnalytics = () => {
  if (!TRACKING_IDS.GA_MEASUREMENT_ID) return;
  
  // Check if already loaded
  if (window.gtag) return;
  
  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${TRACKING_IDS.GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  
  gtag('js', new Date());
  gtag('config', TRACKING_IDS.GA_MEASUREMENT_ID, {
    anonymize_ip: true, // GDPR compliance
    cookie_flags: 'SameSite=None;Secure'
  });
  
  console.log('✅ Google Analytics initialized');
};

// Initialize Facebook Pixel
export const initFacebookPixel = () => {
  if (!TRACKING_IDS.FB_PIXEL_ID) return;
  
  // Check if already loaded
  if (window.fbq) return;
  
  // Facebook Pixel Code
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', TRACKING_IDS.FB_PIXEL_ID);
  window.fbq('track', 'PageView');
  
  console.log('✅ Facebook Pixel initialized');
};

// Initialize Google Ads
export const initGoogleAds = () => {
  if (!TRACKING_IDS.GOOGLE_ADS_ID) return;
  
  // Google Ads uses the same gtag as Google Analytics
  if (window.gtag) {
    window.gtag('config', TRACKING_IDS.GOOGLE_ADS_ID);
    console.log('✅ Google Ads initialized');
  }
};

// Track custom events
export const trackEvent = (eventName, eventParams = {}) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
  
  // Facebook Pixel
  if (window.fbq) {
    window.fbq('track', eventName, eventParams);
  }
};

// Track page views
export const trackPageView = (pagePath) => {
  if (window.gtag) {
    window.gtag('config', TRACKING_IDS.GA_MEASUREMENT_ID, {
      page_path: pagePath
    });
  }
  
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
};

// Track conversions (for Google Ads)
export const trackConversion = (conversionLabel = TRACKING_IDS.GOOGLE_ADS_LABEL) => {
  if (window.gtag && TRACKING_IDS.GOOGLE_ADS_ID && conversionLabel) {
    window.gtag('event', 'conversion', {
      'send_to': `${TRACKING_IDS.GOOGLE_ADS_ID}/${conversionLabel}`
    });
    console.log('✅ Conversion tracked');
  }
};

// Predefined event trackers for common actions
export const trackPhoneClick = () => {
  trackEvent('phone_click', {
    event_category: 'engagement',
    event_label: 'Phone Button'
  });
};

export const trackWhatsAppClick = () => {
  trackEvent('whatsapp_click', {
    event_category: 'engagement',
    event_label: 'WhatsApp Button'
  });
};

export const trackSmileSelfieOpen = () => {
  trackEvent('smile_selfie_open', {
    event_category: 'engagement',
    event_label: 'Smile Selfie Feature'
  });
};

export const trackSmileSelfieCapture = () => {
  trackEvent('smile_selfie_capture', {
    event_category: 'conversion',
    event_label: 'Photo Captured'
  });
};

export const trackVoiceNoteOpen = () => {
  trackEvent('voice_note_open', {
    event_category: 'engagement',
    event_label: 'Voice Note Feature'
  });
};

export const trackVoiceNoteRecord = () => {
  trackEvent('voice_note_record', {
    event_category: 'conversion',
    event_label: 'Voice Note Recorded'
  });
};

export const trackBookingClick = () => {
  trackEvent('booking_click', {
    event_category: 'conversion',
    event_label: 'Book Appointment Button'
  });
  trackConversion(); // Also track as Google Ads conversion
};

export const trackServiceView = (serviceName) => {
  trackEvent('service_view', {
    event_category: 'engagement',
    event_label: serviceName
  });
};

// Initialize all tracking based on consent
export const initializeTracking = (consent) => {
  if (!consent) return;
  
  // Always load analytics if accepted
  if (consent.analytics) {
    initGoogleAnalytics();
    initGoogleAds();
  }
  
  // Load marketing pixels if accepted
  if (consent.marketing) {
    initFacebookPixel();
  }
};

// Remove tracking (for when user withdraws consent)
export const removeTracking = () => {
  // Disable Google Analytics
  if (window.gtag) {
    window['ga-disable-' + TRACKING_IDS.GA_MEASUREMENT_ID] = true;
  }
  
  // Clear Facebook Pixel
  if (window.fbq) {
    delete window.fbq;
  }
  
  console.log('🔒 Tracking disabled');
};
